// components/AssetValidator.tsx
import React, { useState } from 'react';
import { 
	Box, 
	Button, 
	Alert, 
	AlertIcon, 
	VStack, 
	HStack,
	Text, 
	Badge,
	Accordion,
	AccordionItem,
	AccordionButton,
	AccordionPanel,
	AccordionIcon,
	List,
	ListItem,
	ListIcon
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon, SmallCloseIcon } from '@chakra-ui/icons';
import { PersonaAsset, AssetValidationResult } from '../types';
import AssetServices from '../services/AssetServices';
import AssetRenderer from './AssetRenderer';

interface AssetValidatorProps {
	asset: PersonaAsset;
	onValidationComplete?: (result: AssetValidationResult) => void;
}

const AssetValidator: React.FC<AssetValidatorProps> = ({ asset, onValidationComplete }) => {
	const [isValidating, setIsValidating] = useState(false);
	const [validationResult, setValidationResult] = useState<AssetValidationResult | null>(null);
	const [renderErrors, setRenderErrors] = useState<string[]>([]);

	const validateAsset = async () => {
		setIsValidating(true);
		const errors: string[] = [];

		try {
			// Basic validation checks
			await performBasicValidation(errors);
			
			// Type-specific validation
			await performTypeSpecificValidation(errors);
			
			// Dependencies validation
			await performDependencyValidation(errors);

			const result: AssetValidationResult = {
				asset_id: asset.id,
				is_valid: errors.length === 0 && renderErrors.length === 0,
				errors: [...errors, ...renderErrors],
				validation_timestamp: Date.now(),
			};

			setValidationResult(result);
			onValidationComplete?.(result);
		} catch (error) {
			const errorMsg = error instanceof Error ? error.message : 'Unknown validation error';
			errors.push(errorMsg);
			
			const result: AssetValidationResult = {
				asset_id: asset.id,
				is_valid: false,
				errors,
				validation_timestamp: Date.now(),
			};

			setValidationResult(result);
			onValidationComplete?.(result);
		} finally {
			setIsValidating(false);
		}
	};

	const performBasicValidation = async (errors: string[]) => {
		// Check required fields
		if (!asset.name.trim()) {
			errors.push('Asset name is required');
		}

		if (!asset.content.trim()) {
			errors.push('Asset content is required');
		}

		if (!asset.type) {
			errors.push('Asset type is required');
		}

		// Check name format
		if (asset.name.length < 3) {
			errors.push('Asset name must be at least 3 characters long');
		}

		if (!/^[a-zA-Z0-9\s\-_]+$/.test(asset.name)) {
			errors.push('Asset name contains invalid characters');
		}

		// Check version format
		if (!/^\d+\.\d+\.\d+$/.test(asset.version)) {
			errors.push('Asset version must follow semantic versioning (e.g., 1.0.0)');
		}

		// Validate JSON fields
		try {
			AssetServices.parseAssetMetadata(asset.metadata);
		} catch (error) {
			errors.push('Invalid metadata JSON format');
		}

		try {
			AssetServices.parseAssetDependencies(asset.dependencies);
		} catch (error) {
			errors.push('Invalid dependencies JSON format');
		}

		try {
			AssetServices.parseAssetTags(asset.tags);
		} catch (error) {
			errors.push('Invalid tags JSON format');
		}
	};

	const performTypeSpecificValidation = async (errors: string[]) => {
		switch (asset.type) {
			case 'html':
				validateHTML(errors);
				break;
			case 'css':
				validateCSS(errors);
				break;
			case 'javascript':
				validateJavaScript(errors);
				break;
			case 'react-component':
				validateReactComponent(errors);
				break;
			case 'system-prompt-fragment':
				validateSystemPrompt(errors);
				break;
			case 'media':
				validateMedia(errors);
				break;
		}
	};

	const validateHTML = (errors: string[]) => {
		// Check for basic HTML structure
		if (!asset.content.includes('<') || !asset.content.includes('>')) {
			errors.push('HTML content appears to be missing HTML tags');
		}

		// Check for potentially dangerous content
		const dangerousPatterns = ['<script', 'javascript:', 'onload=', 'onerror='];
		dangerousPatterns.forEach(pattern => {
			if (asset.content.toLowerCase().includes(pattern)) {
				errors.push(`Potentially dangerous HTML pattern detected: ${pattern}`);
			}
		});

		// Check template variables
		const metadata = AssetServices.parseAssetMetadata(asset.metadata);
		if (metadata.template_variables) {
			metadata.template_variables.forEach((variable: string) => {
				if (!asset.content.includes(`{{${variable}}}`)) {
					errors.push(`Template variable {{${variable}}} not found in content`);
				}
			});
		}
	};

	const validateCSS = (errors: string[]) => {
		// Basic CSS syntax check
		const braceCount = (asset.content.match(/{/g) || []).length - (asset.content.match(/}/g) || []).length;
		if (braceCount !== 0) {
			errors.push('CSS has unmatched braces');
		}

		// Check for dangerous CSS
		if (asset.content.includes('@import') && !asset.content.includes('/* @import allowed */')) {
			errors.push('CSS @import detected - ensure this is intentional');
		}

		// Validate selector format
		const metadata = AssetServices.parseAssetMetadata(asset.metadata);
		if (metadata.applies_to && !asset.content.includes(metadata.applies_to)) {
			errors.push(`CSS does not contain the specified selector: ${metadata.applies_to}`);
		}
	};

	const validateJavaScript = (errors: string[]) => {
		// Basic syntax check by trying to parse
		try {
			new Function(asset.content);
		} catch (error) {
			errors.push(`JavaScript syntax error: ${error}`);
		}

		// Check for dangerous patterns
		const dangerousPatterns = ['eval(', 'innerHTML', 'document.write', 'setTimeout(', 'setInterval('];
		dangerousPatterns.forEach(pattern => {
			if (asset.content.includes(pattern)) {
				errors.push(`Potentially dangerous JavaScript pattern: ${pattern}`);
			}
		});
	};

	const validateReactComponent = (errors: string[]) => {
		// Basic React component structure check
		if (!asset.content.includes('const Component') && !asset.content.includes('function Component')) {
			errors.push('React component must define a Component function');
		}

		if (!asset.content.includes('return (') && !asset.content.includes('return <')) {
			errors.push('React component must have a return statement');
		}

		// Check for React hooks usage
		const metadata = AssetServices.parseAssetMetadata(asset.metadata);
		if (metadata.requires_react && !asset.content.includes('React')) {
			errors.push('Component marked as requiring React but React is not referenced');
		}
	};

	const validateSystemPrompt = (errors: string[]) => {
		// Check minimum length
		if (asset.content.length < 10) {
			errors.push('System prompt fragment is too short');
		}

		// Check for placeholder content
		if (asset.content.toLowerCase().includes('lorem ipsum') || 
			asset.content.toLowerCase().includes('sample') ||
			asset.content.toLowerCase().includes('placeholder')) {
			errors.push('System prompt appears to contain placeholder content');
		}

		// Validate conditional logic if present
		const metadata = AssetServices.parseAssetMetadata(asset.metadata);
		if (metadata.conditional) {
			try {
				// Basic validation of conditional syntax
				if (!metadata.conditional.includes('context.')) {
					errors.push('Conditional logic should reference context object');
				}
			} catch (error) {
				errors.push('Invalid conditional logic format');
			}
		}
	};

	const validateMedia = (errors: string[]) => {
		// Basic media validation
		if (asset.content.length < 100) {
			errors.push('Media content appears to be too small');
		}

		// Check for base64 encoding if expected
		const metadata = AssetServices.parseAssetMetadata(asset.metadata);
		if (metadata.encoding === 'base64' && !asset.content.startsWith('data:')) {
			errors.push('Media marked as base64 but does not have data URI format');
		}
	};

	const performDependencyValidation = async (errors: string[]) => {
		const dependencies = AssetServices.parseAssetDependencies(asset.dependencies);
		
		if (dependencies.length > 0) {
			// For now, just warn about dependencies
			errors.push(`Asset has ${dependencies.length} dependencies that may need validation`);
		}
	};

	const handleRenderError = (error: string) => {
		setRenderErrors(prev => [...prev, error]);
	};

	const handleRenderSuccess = () => {
		setRenderErrors([]); // Clear render errors on successful render
	};

	return (
		<VStack spacing={4} align="stretch">
			<HStack>
				<Text fontSize="lg" fontWeight="bold">Asset Validation</Text>
				<Badge colorScheme={validationResult?.is_valid ? 'green' : 'red'}>
					{validationResult ? (validationResult.is_valid ? 'Valid' : 'Invalid') : 'Not Validated'}
				</Badge>
			</HStack>

			<Button
				onClick={validateAsset}
				isLoading={isValidating}
				loadingText="Validating..."
				colorScheme="blue"
				size="md"
			>
				Validate Asset
			</Button>

			{validationResult && (
				<Alert status={validationResult.is_valid ? 'success' : 'error'}>
					<AlertIcon />
					<Box>
						<Text fontWeight="bold">
							Validation {validationResult.is_valid ? 'Passed' : 'Failed'}
						</Text>
						{validationResult.errors.length > 0 && (
							<Box mt={2}>
								<Text fontSize="sm" fontWeight="bold">Issues found:</Text>
								<List spacing={1} fontSize="sm">
									{validationResult.errors.map((error, index) => (
										<ListItem key={index}>
											<ListIcon as={SmallCloseIcon} color="red.500" />
											{error}
										</ListItem>
									))}
								</List>
							</Box>
						)}
					</Box>
				</Alert>
			)}

			<Accordion allowToggle>
				<AccordionItem>
					<AccordionButton>
						<Box flex="1" textAlign="left">
							Asset Preview & Render Test
						</Box>
						<AccordionIcon />
					</AccordionButton>
					<AccordionPanel pb={4}>
						<AssetRenderer 
							asset={asset} 
							onError={handleRenderError}
							onSuccess={handleRenderSuccess}
						/>
					</AccordionPanel>
				</AccordionItem>
			</Accordion>
		</VStack>
	);
};

export default AssetValidator;